/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Button,
  IconButton,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import style from "../../../styles/common/formModal.module.css";
import EditDialog from "../../../Components/Common/EditDialog";
import SelectField from "../../../Components/Masters/SelectField";
import countries from "../../../Pages/Masters/Roles and Permissions/Country.json";
import FileField from "../../../Components/Masters/FileField";
import formatTitleWithOptional from "../../Scope1/OptionalFeild";

const buildReportingStartYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;
  const startYear = 2020;
  const totalYears = Math.max(0, lastYear - startYear + 1);
  return Array.from({ length: totalYears }, (_, i) => startYear + i);
};

const buildReportingEndYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const startYear = 2021;
  const totalYears = Math.max(0, currentYear - startYear + 1);
  return Array.from({ length: totalYears }, (_, i) => startYear + i);
};

const inferReportingStartYear = (iso, toDateInputValueFn) => {
  const full = toDateInputValueFn(iso);
  if (!full) return "";
  const [ys, ms, ds] = full.split("-");
  const y = parseInt(ys, 10);
  const m = parseInt(ms, 10);
  const d = parseInt(ds, 10);
  if (m === 4 && d === 1) return ys;
  if (m > 4 || (m === 4 && d > 1)) return String(y);
  if (m < 4) return String(y - 1);
  return String(y);
};

const inferReportingEndYear = (iso, toDateInputValueFn) => {
  const full = toDateInputValueFn(iso);
  if (!full) return "";
  const [ys, ms, ds] = full.split("-");
  const y = parseInt(ys, 10);
  const m = parseInt(ms, 10);
  const d = parseInt(ds, 10);
  if (m === 3 && d === 31) return ys;
  if (m < 3 || (m === 3 && d < 31)) return String(y);
  return String(y + 1);
};

const Form = ({
  isOpen,
  onClose,
  onSubmit,
  header,
  initialData,
  isEditing,
  franchiseOptions = [],
  onSearchFranchises = () => {},
}) => {
  const MASTER_FILLED_FIELDS = [
    "franchiseName",
    "franchiseId",
    "country",
    "locationAddress",
    "contactPerson",
    "franchiseType",
  ];
  const toDateInputValue = (value) => {
    if (!value) return "";

    if (typeof value === "string") {
      const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (isoMatch) return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().split("T")[0];
  };

  const initialFormData = {
    franchiseMaster:
      typeof initialData?.franchiseMaster === "object"
        ? initialData?.franchiseMaster?._id || ""
        : initialData?.franchiseMaster || "",
    franchiseName: initialData?.franchiseName || "",
    franchiseId: initialData?.franchiseId || "",
    country: initialData?.country || "",
    locationAddress: initialData?.locationAddress || "",
    reportingStartYear: inferReportingStartYear(
      initialData?.reportingStart,
      toDateInputValue
    ),
    reportingEndYear: inferReportingEndYear(
      initialData?.reportingEnd,
      toDateInputValue
    ),
    contactPerson: initialData?.contactPerson || "",
    franchiseType: initialData?.franchiseType || "",
    scope1EmissionsMetricTon: initialData?.scope1EmissionsMetricTon || "",
    scope2EmissionsMetricTon: initialData?.scope2EmissionsMetricTon || "",
    scope3EmissionsMetricTon: initialData?.scope3EmissionsMetricTon || "",
    documents: initialData?.documents || [],
  };

  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [formDataSnapshot, setFormDataSnapshot] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [franchiseSearchText, setFranchiseSearchText] = useState("");

  useEffect(() => {
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles(initialData?.documents || []);
    setFranchiseSearchText(initialData?.franchiseId || "");
  }, [isOpen, initialData]);

  const applySelectedFranchise = (selected) => {
    if (!selected) return;
    setFormData((prev) => ({
      ...prev,
      franchiseMaster: selected?._id || prev.franchiseMaster,
      franchiseName: selected.franchiseName || prev.franchiseName,
      franchiseId: selected.franchiseId || prev.franchiseId,
      country: selected.country || prev.country,
      locationAddress: selected.locationAddress || prev.locationAddress,
      // Reporting period should be entered by the user (not sourced from master)
      reportingStartYear: "",
      reportingEndYear: "",
      contactPerson: selected.contactPerson || prev.contactPerson,
      franchiseType: selected.franchiseType || prev.franchiseType,
    }));
    setErrors((prev) => ({
      ...prev,
      franchiseName: "",
      franchiseId: "",
      country: "",
      locationAddress: "",
      reportingStartYear: "",
      reportingEndYear: "",
      contactPerson: "",
      franchiseType: "",
    }));
  };

  const fieldConfig = [
    {
      label: "Franchise ID",
      name: "franchiseId",
      type: "text",
      required: true,
      placeholder: "Enter franchise ID",
    },
    {
      label: "Franchise Name",
      name: "franchiseName",
      type: "text",
      required: true,
      placeholder: "Enter franchise name",
    },
    {
      label: "Country",
      name: "country",
      type: "select",
      required: true,
      placeholder: "Enter country",
    },
    {
      label: "Location/Address",
      name: "locationAddress",
      type: "text",
      required: true,
      placeholder: "Enter location/address",
    },
    {
      label: "Reporting Period Start Date",
      name: "reportingStartYear",
      type: "reportingYear",
      fixedDateText: "1st April",
      required: true,
    },
    {
      label: "Reporting Period End Date",
      name: "reportingEndYear",
      type: "reportingYear",
      fixedDateText: "31st March",
      required: true,
    },
    {
      label: "Contact Person",
      name: "contactPerson",
      type: "text",
      required: true,
      placeholder: "Enter contact person",
    },
    {
      label: "Franchise Type",
      name: "franchiseType",
      type: "text",
      required: true,
      placeholder: "Enter franchise type",
    },
    {
      label: "Scope 1 Emissions (Metric Ton)",
      name: "scope1EmissionsMetricTon",
      type: "text",
      required: true,
      placeholder: "Enter value",
      numericOnly: true,
    },
    {
      label: "Scope 2 Emissions (Metric Ton)",
      name: "scope2EmissionsMetricTon",
      type: "text",
      required: true,
      placeholder: "Enter value",
      numericOnly: true,
    },
    {
      label: "Scope 3 Emissions (Metric Ton)",
      name: "scope3EmissionsMetricTon",
      type: "text",
      required: false,
      placeholder: "Enter value",
      numericOnly: true,
    },
    {
      name: "document",
      type: "docs",
      placeholder: "Enter Document",
    },
  ];

  const handleExitAgree = () => {
    setEditDialog(false);
    setErrors({});
    setFormData(initialFormData);
    setFormDataSnapshot(initialFormData);
    setUploadedFiles([]);
    onClose();
  };

  const isFormChanged = () =>
    JSON.stringify(formData) !== JSON.stringify(formDataSnapshot);

  const handleClose = () => {
    if (!isFormChanged()) {
      handleExitAgree();
    } else {
      setEditDialog(true);
    }
  };

  const validateFormData = () => {
    const newErrors = {};

    fieldConfig.forEach((field) => {
      const value = formData[field.name];

      if (field.required) {
        if (
          value === "" ||
          value === null ||
          value === undefined ||
          value === "Tap to select"
        ) {
          newErrors[field.name] = `${field.label} is required.`;
          return;
        }
      }

      if ((field.type === "number" || field.numericOnly) && value !== "" && isNaN(Number(value))) {
        newErrors[field.name] = `${field.label} must be a number.`;
      }
    });

    const startIso =
      formData.reportingStartYear &&
      `${formData.reportingStartYear}-04-01`;
    const endIso =
      formData.reportingEndYear && `${formData.reportingEndYear}-03-31`;
    if (startIso && endIso && new Date(endIso) < new Date(startIso)) {
      newErrors.reportingEndYear =
        "Reporting period end (31st March) must fall on or after the selected start year (1st April).";
    }

    if (formData.reportingStartYear && formData.reportingEndYear) {
      const startYear = parseInt(formData.reportingStartYear, 10);
      const endYear = parseInt(formData.reportingEndYear, 10);
      if (!Number.isNaN(startYear) && !Number.isNaN(endYear) && endYear !== startYear + 1) {
        newErrors.reportingEndYear =
          "Reporting period end year must be the immediate next year of the start year.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (name, type) => (event) => {
    const { value } = event.target;

    // Check if field should only accept numeric values
    const field = fieldConfig.find((f) => f.name === name);
    const isNumericField = type === "number" || field?.numericOnly;

    if (isNumericField && value !== "" && !/^\d*\.?\d*$/.test(value)) return;

    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };

      if (name === "reportingStartYear") {
        const startYear = parseInt(value, 10);
        const endYear = parseInt(prev.reportingEndYear, 10);
        if (!Number.isNaN(startYear) && !Number.isNaN(endYear) && endYear !== startYear + 1) {
          updated.reportingEndYear = "";
        }
      }

      if (name === "reportingStartYear" || name === "reportingEndYear") {
        setErrors((prevErrors) => ({
          ...prevErrors,
          reportingEndYear: "",
        }));
      }

      return updated;
    });

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = () => {
    if (!validateFormData()) return;
    
    let formDataToSubmit = {
      ...formData,
      reportingPeriodStart: formData.reportingStartYear
        ? `${formData.reportingStartYear}-04-01`
        : "",
      reportingPeriodEnd: formData.reportingEndYear
        ? `${formData.reportingEndYear}-03-31`
        : "",
      documents: uploadedFiles,
    };
    delete formDataToSubmit.reportingStartYear;
    delete formDataToSubmit.reportingEndYear;

    onSubmit(formDataToSubmit, isEditing);
    setUploadedFiles([]);
  };

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={isOpen}
      onClose={handleClose}
    >
      <Box
        sx={{
          width: "26rem",
          display: "flex",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        {editDialog && (
          <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
        )}
        <Box
          sx={{
            backgroundColor: "#fff",
            height: "20px",
            padding: "13px",
            margin: "30px 0 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "80%",
            width: "5%",
            zIndex: "1",
          }}
        >
          <IconButton onClick={handleClose} sx={{ color: "black" }}>
            <CloseIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Box>
        <Box className={style.main_div}>
          <Typography
            id="modal-title"
            variant="h6"
            align="left"
            sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}
          >
            {header}
          </Typography>

          <form>
            {fieldConfig.map((field) => (
              <div key={field.name} style={{ margin: "1rem 0" }}>
                {field.type === "select" && field.name === "country" ? (
                  <SelectField
                    title={field.label}
                    name={field.name}
                    value={formData[field.name]}
                    disable={true}
                    handleChange={(e) => {
                      const value = e.target.value === "Tap to select" ? "" : e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        [field.name]: value,
                      }));
                      setErrors((prev) => ({
                        ...prev,
                        [field.name]: "",
                      }));
                    }}
                    options={countries.countries.map((country) => country.name)}
                    errors={errors[field.name]}
                  />
                ) : field.name === "franchiseName" ? (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 1 }}
                    >
                      {field.label}
                    </Typography>

                    <TextField
                      type="text"
                      size="small"
                      fullWidth
                      required={field.required}
                      disabled
                      placeholder="Franchise name"
                      value={formData[field.name] || ""}
                      onChange={handleChange(field.name, field.type)}
                      error={Boolean(errors[field.name])}
                      helperText={errors[field.name] || ""}
                      FormHelperTextProps={{
                        sx: { ml: 0, mr: 0, fontSize: "0.875rem" },
                      }}
                    />
                  </>
                ) : field.name === "franchiseId" ? (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 1 }}
                    >
                      {field.label}
                    </Typography>
                    <Autocomplete
                      size="small"
                      options={franchiseOptions}
                      value={
                        franchiseOptions.find(
                          (option) => option?.franchiseId === formData.franchiseId
                        ) || null
                      }
                      inputValue={franchiseSearchText}
                      onInputChange={(event, newInputValue) => {
                        setFranchiseSearchText(newInputValue);
                        setFormData((prev) => ({
                          ...prev,
                          franchiseMaster: "",
                          franchiseId: newInputValue,
                          franchiseName: "",
                        }));
                        onSearchFranchises(newInputValue);
                      }}
                      onChange={(event, newValue) => {
                        applySelectedFranchise(newValue);
                        setFranchiseSearchText(newValue?.franchiseId || "");
                      }}
                      getOptionLabel={(option) => option?.franchiseId || ""}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          placeholder="Tap to select"
                          error={Boolean(errors.franchiseId)}
                          helperText={errors.franchiseId || ""}
                          FormHelperTextProps={{
                            sx: { ml: 0, mr: 0, fontSize: "0.875rem" },
                          }}
                        />
                      )}
                      sx={{
                        width: "100%",
                        "& .MuiAutocomplete-inputRoot": {
                          height: 40,
                          alignItems: "center",
                        },
                      }}
                    />
                  </>
                ) : field.type === "docs" ? (
                  <FileField
                    title={formatTitleWithOptional("Upload Documents (optional)")}
                    name={field.name}
                    errors={errors[field.name]}
                    fileUrls={uploadedFiles}
                    progress={uploadProgress[field.name] || []}
                    {...{
                      setUploadedFiles,
                      setUploadProgress,
                      setErrors,
                    }}
                  />
                ) : field.type === "reportingYear" ? (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 1 }}
                    >
                      {field.label}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        flexWrap: "nowrap",
                      }}
                    >
                      <TextField
                        size="small"
                        value={field.fixedDateText}
                        disabled
                        sx={{
                          width: 130,
                          "& .MuiInputBase-input.Mui-disabled": {
                            WebkitTextFillColor: "rgba(0, 0, 0, 0.87)",
                            color: "rgba(0, 0, 0, 0.87)",
                          },
                          "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline":
                            {
                              borderColor: "rgba(0, 0, 0, 0.23)",
                            },
                        }}
                      />
                      <TextField
                        select
                        size="small"
                        required={field.required}
                        sx={{ width: 130 }}
                        value={formData[field.name] || ""}
                        onChange={handleChange(field.name, field.type)}
                        error={Boolean(errors[field.name])}
                        FormHelperTextProps={{
                          sx: { ml: 0, mr: 0, fontSize: "0.875rem" },
                        }}
                        SelectProps={{ displayEmpty: true }}
                      >
                        <MenuItem value="">
                          <em>Select year</em>
                        </MenuItem>
                        {(field.name === "reportingStartYear"
                          ? buildReportingStartYearOptions()
                          : buildReportingEndYearOptions())
                          .filter((yr) => {
                            if (field.name !== "reportingEndYear") return true;
                            const startYear = parseInt(formData.reportingStartYear, 10);
                            if (Number.isNaN(startYear)) return true;
                            return yr === startYear + 1;
                          })
                          .map((yr) => (
                          <MenuItem key={yr} value={String(yr)}>
                            {yr}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    {errors[field.name] ? (
                      <Typography
                        variant="body2"
                        color="#D34040"
                        sx={{ mt: 0.5, ml: 0,}}
                      >
                        {errors[field.name]}
                      </Typography>
                    ) : null}
                  </>
                ) : (
                  <>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", mt: 1 }}
                    >
                      {field.label}
                    </Typography>

                    <TextField
                      type="text"
                      size="small"
                      fullWidth
                      required={field.required}
                      disabled={MASTER_FILLED_FIELDS.includes(field.name)}
                      placeholder={field.placeholder || ""}
                      value={formData[field.name] || ""}
                      onChange={handleChange(field.name, field.type)}
                      error={Boolean(errors[field.name])}
                      helperText={errors[field.name] || ""}
                      FormHelperTextProps={{
                        sx: { ml: 0, mr: 0, fontSize: "0.875rem" },
                      }}
                    />
                  </>
                )}
              </div>
            ))}
          </form>

          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ backgroundColor: "#1C486B", textTransform: "none" }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Form;

